import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Plus } from "lucide-react"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useSubstanceArticles } from "../../../api/substancesApi"
import type { ArticleRow } from "../../../api/types/masterData"
import {
  EntitySubTable,
  type SubTableColumn,
} from "../../shared/EntitySubTable"
import { UnlinkArticleSubstanceButton } from "../../shared/UnlinkArticleSubstanceButton"
import { LinkSubstanceDialog } from "./LinkSubstanceDialog"

export function SubstanceArticlesSection() {
  const [searchParams] = useSearchParams()
  const substanceId = searchParams.get("id")
  const navigate = useNavigate()
  const { translate } = useTranslation()
  const canEdit = useFeatureFlag("MC_MASTER_DATA_SUBSTANCE_EDIT")
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data, hasNextPage, fetchNextPage, isLoading } = useSubstanceArticles(
    substanceId,
    { limit: 20 },
  )

  const articles = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total

  const columns: SubTableColumn<ArticleRow>[] = [
    {
      key: "articleNumber",
      header: translate("MC_MD_PANEL_COL_ARTICLE"),
      cell: (a) => <span className="font-mono">{a.articleNumber}</span>,
      className: "w-60",
    },
    {
      key: "name",
      header: translate("MC_MD_PANEL_COL_DESCRIPTION"),
      cell: (a) => a.name,
    },
    {
      key: "supplier",
      header: translate("MC_MD_PANEL_COL_SUPPLIER"),
      cell: (a) => a.supplierName ?? <span className="text-grey5">—</span>,
      className: "w-36",
    },
  ]

  if (canEdit) {
    columns.push({
      key: "unlink",
      header: "",
      // `linkId` is nullable on `ArticleRow` (only GET /substances/{id}/articles
      // populates it — see the type's own doc comment), so this early-return
      // preserves what the original per-file button already did: nothing to
      // unlink without a link id, so render nothing rather than guess one.
      cell: (a) =>
        a.linkId ? (
          <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
            <UnlinkArticleSubstanceButton
              articleId={a.id}
              linkId={a.linkId}
              source={a.linkSource ?? "manual"}
              itemLabel={a.articleNumber}
            />
          </PermissionGate>
        ) : null,
      className: "w-10 text-right",
    })
  }

  const addButton = canEdit ? (
    <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
      <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
        <Plus />
        {translate("MC_MD_PANEL_WHEREUSED_LINK")}
      </Button>
    </PermissionGate>
  ) : undefined

  return (
    <>
      <EntitySubTable
        title={translate("MC_MD_PANEL_WHEREUSED_TITLE")}
        items={articles}
        columns={columns}
        // The same article can legitimately appear more than once here (once
        // per supplier declaring this substance for it), so `a.id` can repeat
        // across rows — `linkId` is the actually-unique per-row identifier.
        // See mc_article_repository.py's list_substance_articles_by_id.
        keyOf={(a) => a.linkId ?? a.id}
        hasMore={!!hasNextPage}
        onFetchMore={fetchNextPage}
        isLoading={isLoading}
        total={total}
        onRowClick={(a) => navigate(`../articles?id=${a.id}&source=nav`)}
        action={addButton}
      />
      {canEdit && (
        <LinkSubstanceDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          substanceId={substanceId ?? undefined}
          // Redundant with onOpenChange(false) inside the dialog's own success
          // handler today, but kept as the extension point for anything this
          // section wants to do specifically on a successful link later (a
          // toast, scrolling the new row into view, …) without touching the
          // dialog itself.
          onLinked={() => setDialogOpen(false)}
          // The substance page had no handler at all, so "already linked" ended
          // on a form that could never succeed. It cannot open the row in place
          // — the concentration editor lives on the article — so it hands the
          // article page the link to open (F14).
          onExistingLink={(linkId) => {
            setDialogOpen(false)
            const existing = articles.find((a) => a.linkId === linkId)
            if (existing)
              navigate(`../articles?id=${existing.id}&edit=${linkId}`)
          }}
        />
      )}
    </>
  )
}
